import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Terms() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <div className="mb-8">
        <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Home
        </Link>
      </div>

      <h1 className="text-3xl font-bold mb-6">Terms of Service</h1>
      <div className="text-sm text-gray-600 mb-8">
        Last updated: April 29, 2026
      </div>

      <div className="prose prose-lg max-w-none">
        <p>
          Welcome to Fuel Central, owned and operated by GSL Solutions, Inc. ("GSL Solutions, Inc.", "we", "our", or "us").
          These Terms of Service ("Terms") govern your access to and use of our websites, mobile applications, and other online
          products and services (collectively, the "Services").
        </p>

        <p>
          By clicking "I Accept" (or similar language) or by accessing or using our Services, you agree to be bound by these
          Terms and all terms incorporated by reference. If you do not agree to these Terms, do not access or use our Services.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">1. Eligibility</h2>
        <p>
          You must be at least 18 years of age to access or use our Services. By accessing or using our Services, you represent
          and warrant that you are at least 18 years old and have the legal capacity to enter into a binding agreement with us.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">2. Account Registration</h2>
        <p>
          To access certain features of our Services, you may need to register for an account. When you register, you agree to provide
          accurate, current, and complete information about yourself and to update this information to keep it accurate, current, and complete.
          You are responsible for safeguarding your account credentials and for all activities that occur under your account. You agree to
          notify us immediately of any unauthorized use of your account or any other breach of security.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">3. User Content</h2>
        <p>
          Our Services may allow you to upload, submit, store, send, or receive content ("User Content"). You retain ownership
          of any intellectual property rights that you hold in User Content. By submitting User Content to or through our Services,
          you grant us a worldwide, non-exclusive, royalty-free license (with the right to sublicense) to use, copy, modify, publicly
          display, publicly perform, and distribute your User Content in connection with operating and providing our Services.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">4. Fees, Billing, and Subscriptions</h2>
        <p>
          Certain features of our Services require payment. Fees are displayed at the point of purchase and are payable in U.S. dollars.
          By submitting payment information, you authorize GSL Solutions, Inc. to charge the payment method you provide for all fees incurred.
        </p>

        <h3 className="text-lg font-semibold mt-6 mb-3">(a) Single Valuations</h3>
        <p>
          Single valuation estimates are charged on a per-valuation basis at the rate displayed at checkout (currently $100 per valuation).
          Payment is due in full at the time of order. All sales are final upon successful generation of the on-screen estimate, except
          as provided in Section 5 (Refunds).
        </p>

        <h3 className="text-lg font-semibold mt-6 mb-3">(b) Subscriptions</h3>
        <p>
          Subscription plans are billed on a recurring monthly basis at the rate displayed at signup (currently $500 per month).
          Subscriptions automatically renew on the same date each billing cycle until cancelled by you. You may cancel at any time via
          your account settings or by emailing us at the address in Section 18. Cancellation takes effect at the end of the then-current
          billing period.
        </p>

        <h3 className="text-lg font-semibold mt-6 mb-3">(c) Subscription Inclusions</h3>
        <p>
          Subscription plans include the number of valuation estimates per billing cycle stated at signup. Unused valuations do not roll
          over to subsequent billing cycles unless expressly stated. Subscription benefits are non-transferable and have no cash value.
        </p>

        <h3 className="text-lg font-semibold mt-6 mb-3">(d) Failed Payments</h3>
        <p>
          If a payment fails or is reversed, we may suspend access to the Services until payment is resolved. Repeated payment failures
          may result in account termination.
        </p>

        <h3 className="text-lg font-semibold mt-6 mb-3">(e) Taxes</h3>
        <p>
          Fees do not include any taxes. You are responsible for any applicable sales, use, or similar taxes.
        </p>

        <h3 className="text-lg font-semibold mt-6 mb-3">(f) Payment Processing</h3>
        <p>
          Payments are processed by third-party processors (currently Stripe). By providing payment information, you also agree to the
          applicable processor's terms.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">5. Refunds</h2>

        <h3 className="text-lg font-semibold mt-6 mb-3">(a) Single Valuations</h3>
        <p>
          All sales of single valuations are final once the on-screen estimate is successfully generated and displayed. We may, in our
          sole discretion, issue a refund in the following limited circumstances, provided you contact us at the email in Section 18
          within seven (7) days of the charge: (i) the valuation failed to generate due to a system error attributable to GSL Solutions, Inc.;
          (ii) the valuation contains a material factual error in the underlying input data; or (iii) the subject property could not be
          valued due to insufficient data inputs and you were charged in error.
        </p>

        <h3 className="text-lg font-semibold mt-6 mb-3">(b) Subscriptions</h3>
        <p>
          You may cancel your subscription at any time, effective at the end of your current billing period. We do not prorate partial
          billing periods, and we do not refund unused subscription benefits or partial-cycle subscription fees.
        </p>

        <h3 className="text-lg font-semibold mt-6 mb-3">(c) No Refunds for Dissatisfaction with Estimates</h3>
        <p>
          Refunds are not available for dissatisfaction with the valuation estimate itself, change of mind, or buyer's remorse.
        </p>

        <h3 className="text-lg font-semibold mt-6 mb-3">(d) Chargebacks</h3>
        <p>
          We request that you contact us before initiating a chargeback with your card issuer. Chargebacks initiated without prior
          good-faith contact may result in account suspension and recovery of associated fees.
        </p>

        <p className="mt-4">
          For full details, see our <Link to="/refund-policy" className="text-blue-600 hover:text-blue-800">Refund Policy</Link>.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">6. Access to Generated Valuations</h2>
        <p>
          Once a valuation has been successfully generated, you may continue to access and view that valuation through your account for
          as long as your account remains in good standing. Access is tied to your account; if your account is terminated for breach of
          these Terms, non-payment, or fraudulent activity, access to historical valuations may be revoked. We do not guarantee perpetual
          availability of historical valuations and reserve the right, with reasonable notice, to archive or migrate older valuations.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">7. Prohibited Conduct</h2>
        <p>You agree not to:</p>
        <ul className="list-disc pl-6 mb-4">
          <li>Use our Services in any manner that could interfere with, disable, disrupt, overburden, or otherwise impair the Services;</li>
          <li>Access or attempt to access any data, accounts, or systems that you are not authorized to access;</li>
          <li>Engage in any activity that is fraudulent, false, misleading, or deceptive;</li>
          <li>Violate any applicable law, regulation, or third-party right;</li>
          <li>Use our Services to distribute malware, viruses, or other harmful computer code;</li>
          <li>Collect or store personal data about other users without their express consent;</li>
          <li>Impersonate any person or entity or misrepresent your affiliation with a person or entity;</li>
          <li>Use automated means, including spiders, robots, crawlers, data mining tools, or similar methods to access our Services, monitor or copy content, or interfere with the operation of our Services;</li>
          <li>Modify, decompile, reverse engineer, or otherwise attempt to derive the source code of our Services;</li>
          <li>Use our Services for any illegal purpose or in violation of these Terms.</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-4">8. Intellectual Property Rights</h2>
        <p>
          The Services and all content, features, and functionality thereof (including, without limitation, all information, software,
          text, displays, images, video, audio, valuation methodology, and the design, selection, and arrangement thereof) are owned by
          GSL Solutions, Inc., its licensors, or other providers of such material and are protected by copyright, trademark, patent,
          trade secret, and other intellectual property or proprietary rights laws. These Terms do not grant you any right, title, or
          interest in or to the Services or any content, features, or functionality thereof, except as expressly set forth in Section 9.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">9. License and Use Restrictions on Valuations</h2>
        <p>
          Subject to your payment of all applicable fees and your compliance with these Terms, GSL Solutions, Inc. grants you a limited,
          non-exclusive, non-transferable, non-sublicensable, revocable license to access and view valuation estimates generated for
          properties you submit, solely for your internal business purposes.
        </p>
        <p>You may not:</p>
        <ul className="list-disc pl-6 mb-4">
          <li>Resell, sublicense, or redistribute valuation estimates to third parties;</li>
          <li>Republish valuation estimates publicly or in marketing materials without our prior written consent;</li>
          <li>Use valuation estimates, screenshots, or extracted data, in whole or in part, to develop, train, validate, or benchmark any competing automated valuation model, machine-learning model, or appraisal product; or</li>
          <li>Systematically scrape, copy, or extract valuation data from the Services through automated means.</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-4">10. Third-Party Links and Services</h2>
        <p>
          Our Services may contain links to third-party websites, services, or content that are not owned or controlled by us. We have
          no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or
          services. You further acknowledge and agree that we shall not be responsible or liable, directly or indirectly, for any damage
          or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods, or services
          available on or through any such websites or services.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">11. Nature of Valuations; Disclaimer of Warranties</h2>

        <h3 className="text-lg font-semibold mt-6 mb-3">(a) Automated Valuation Model</h3>
        <p>
          The Services include an Automated Valuation Model ("AVM") that generates on-screen valuation estimates for retail gasoline
          service stations and related properties based on user-submitted data, third-party market data, and proprietary methodology.
          <strong> Valuation estimates produced by the Services are estimates generated by automated processes. They are not appraisals
          as defined under the Uniform Standards of Professional Appraisal Practice ("USPAP"), are not certified opinions of value, are
          not broker price opinions, and are not investment, legal, tax, accounting, or financial advice.</strong>
        </p>

        <h3 className="text-lg font-semibold mt-6 mb-3">(b) No Reliance for Transaction Decisions</h3>
        <p>
          Valuation estimates should not be used as the sole basis for any purchase, sale, financing, lending, refinancing, investment,
          or business decision. You are solely responsible for conducting your own due diligence and engaging qualified professionals
          (including licensed appraisers, attorneys, accountants, and lenders) before making any such decision. Any reliance on a
          valuation estimate is at your own risk.
        </p>

        <h3 className="text-lg font-semibold mt-6 mb-3">(c) No Fiduciary or Advisory Relationship</h3>
        <p>
          Use of the Services does not create any fiduciary, advisory, brokerage, agency, or appraiser-client relationship between you
          and GSL Solutions, Inc.
        </p>

        <h3 className="text-lg font-semibold mt-6 mb-3">(d) Third-Party Data</h3>
        <p>
          Valuations incorporate data from third-party sources we believe to be reliable but whose accuracy, completeness, and timeliness
          we do not warrant.
        </p>

        <h3 className="text-lg font-semibold mt-6 mb-3">(e) "AS IS" Services</h3>
        <p>
          THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT
          NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF
          PERFORMANCE. GSL SOLUTIONS, INC., ITS AFFILIATES, AND ITS LICENSORS DO NOT WARRANT THAT: (i) THE SERVICES WILL FUNCTION
          UNINTERRUPTED, SECURE, OR AVAILABLE AT ANY PARTICULAR TIME OR LOCATION; (ii) ANY ERRORS OR DEFECTS WILL BE CORRECTED;
          (iii) THE SERVICES ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS; OR (iv) THE RESULTS OF USING THE SERVICES, INCLUDING ANY
          VALUATION ESTIMATES, WILL MEET YOUR REQUIREMENTS OR BE ACCURATE.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">12. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL GSL SOLUTIONS, INC., ITS AFFILIATES, OR ITS LICENSORS BE
          LIABLE FOR ANY INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING WITHOUT LIMITATION
          DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATING TO YOUR ACCESS TO OR
          USE OF, OR INABILITY TO ACCESS OR USE, THE SERVICES.
        </p>
        <p>
          NOTWITHSTANDING ANY OTHER PROVISION OF THESE TERMS, THE TOTAL AGGREGATE LIABILITY OF GSL SOLUTIONS, INC., ITS AFFILIATES, AND
          ITS LICENSORS ARISING FROM OR RELATED TO YOUR USE OF THE SERVICES SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID GSL
          SOLUTIONS, INC. IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100.00).
          YOU AGREE THAT THIS LIMITATION IS A FUNDAMENTAL ELEMENT OF THE BARGAIN BETWEEN YOU AND GSL SOLUTIONS, INC. AND THAT THE
          SERVICES WOULD NOT BE PROVIDED ABSENT SUCH LIMITATION.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">13. Indemnification</h2>
        <p>
          You agree to defend, indemnify, and hold harmless GSL Solutions, Inc., its affiliates, licensors, and service providers, and
          its and their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns
          from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable
          attorneys' fees) arising out of or relating to your violation of these Terms, your use of the Services, or your reliance on
          any valuation estimate generated by the Services.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">14. Termination</h2>
        <p>
          We may terminate or suspend your access to all or part of our Services, without notice or liability, for any reason, including
          without limitation if you breach these Terms. Upon termination, your right to use the Services will immediately cease. All
          provisions of these Terms which by their nature should survive termination shall survive termination, including, without
          limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">15. Changes to Terms</h2>
        <p>
          We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will
          provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at
          our sole discretion. By continuing to access or use our Services after those revisions become effective, you agree to be bound
          by the revised terms.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">16. Governing Law</h2>
        <p>
          These Terms shall be governed and construed in accordance with the laws of the State of California, without regard to its
          conflict of law provisions. Any legal suit, action, or proceeding arising out of or related to these Terms or the Services
          shall be instituted exclusively in the federal courts of the United States or the courts of the State of California, in each
          case located in Orange County.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">17. Severability and Entire Agreement</h2>
        <p>
          If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions shall continue in full force
          and effect. These Terms, together with any policies referenced herein (including our{' '}
          <Link to="/refund-policy" className="text-blue-600 hover:text-blue-800">Refund Policy</Link> and{' '}
          <Link to="/privacy" className="text-blue-600 hover:text-blue-800">Privacy Policy</Link>), constitute the entire agreement
          between you and GSL Solutions, Inc. regarding the Services and supersede all prior agreements and understandings.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">18. Contact Information</h2>
        <p>If you have any questions about these Terms, please contact us at:</p>
        <address className="not-italic mt-2 mb-4">
          GSL Solutions, Inc.<br />
          P.O. Box 3108<br />
          Corona, CA 92878<br />
          Email: TheFuelCentral@gmail.com<br />
          Phone: 951-223-5610
        </address>
      </div>

      <div className="my-8">
        <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Home
        </Link>
      </div>
    </div>
  );
}
